// Dependencies
import { Map } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import React, { ReactElement, useEffect, useRef } from 'react';
import {
    AtomicBlockUtils,
    // Editor,
    EditorState,
    RichUtils,
    DefaultDraftBlockRenderMap,
    DraftHandleValue,
} from 'draft-js';
import Editor, { composeDecorators } from '@draft-js-plugins/editor';
import createAlignmentPlugin from '@draft-js-plugins/alignment';
import createFocusPlugin from '@draft-js-plugins/focus';
import createResizeablePlugin from '@draft-js-plugins/resizeable';
import createBlockDndPlugin from '@draft-js-plugins/drag-n-drop';
import createDragNDropUploadPlugin from '@draft-js-plugins/drag-n-drop-upload';
import mockUpload from './mockUpload';
import createImagePlugin from '@draft-js-plugins/image';
// Core & Lab
import Grid from '@mui/material/Grid';
import decorator from './decorator';
import { RichEditorFontSizeStylesMap } from './richEditorFontSizeControl';
import { EditorToolbar, EditorToolbarProps } from './editorToolbar';
import { renderRichEditorContentHTML } from './renderRichEditorContentHtml';
import { restoreRichEditorStateFromHtml } from './restoreRichEditorStateFromHtml';
import { customColors } from './customColors';
import { Picture } from '../../picture/objects/picture';
import { AppState } from '../../../reducers';
import { Typography } from '@mui/material';
import { isNumber } from 'lodash';
import { useTranslation } from 'react-i18next';


const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin();
const blockDndPlugin = createBlockDndPlugin();
const alignmentPlugin = createAlignmentPlugin();
const { AlignmentTool } = alignmentPlugin;

const imageDecorator = composeDecorators(
    resizeablePlugin.decorator,
    alignmentPlugin.decorator,
    focusPlugin.decorator,
    blockDndPlugin.decorator
);
const imagePlugin = createImagePlugin({ decorator: imageDecorator });

const dragNDropFileUploadPlugin = createDragNDropUploadPlugin({
    handleUpload: mockUpload,
    addImage: imagePlugin.addImage,
});

const plugins = [
    dragNDropFileUploadPlugin,
    blockDndPlugin,
    focusPlugin,
    alignmentPlugin,
    resizeablePlugin,
    imagePlugin,
];

export const RichEditorStyleMap: {[index: string]: React.CSSProperties} = {
    'STRIKETHROUGH': {
        textDecoration: 'line-through',
    },
    'COLOR_BLACK': {
        color: '#000000'
    },
    'COLOR_DARK_GRAY_4': {
        color: '#434343'
    },
    'COLOR_DARK_GRAY_3': {
        color: '#666666'
    },
    'COLOR_DARK_GRAY_2': {
        color: '#999999'
    },
    'COLOR_DARK_GRAY_1': {
        color: '#B7B7B7'
    },
    'COLOR_GRAY': {
        color: '#CCCCCC'
    },
    'COLOR_LIGHT_GRAY_1': {
        color: '#D9D9D9'
    },
    'COLOR_LIGHT_GRAY_2': {
        color: '#EFEFEF'
    },
    'COLOR_LIGHT_GRAY_3': {
        color: '#F3F3F3'
    },
    'COLOR_WHITE': {
        color: '#FFFFFF'
    },
    'COLOR_RED_BERRY': {
        color: '#980000'
    },
    'COLOR_RED': {
        color: '#FF0000'
    },
    'COLOR_ORANGE': {
        color: '#FF9900'
    },
    'COLOR_YELLOW': {
        color: '#FFFF00'
    },
    'COLOR_GREEN': {
        color: '#00FF00'
    },
    'COLOR_CYAN': {
        color: '#00FFFF'
    },
    'COLOR_CORNFLOWER_BLUE': {
        color: '#4A86E8'
    },
    'COLOR_BLUE': {
        color: '#0000FF'
    },
    'COLOR_PURPLE': {
        color: '#9900FF'
    },
    'COLOR_MAGENTA': {
        color: '#FF00FF'
    },
    'COLOR_LIGHT_RED_BERRY_3': {
        color: '#E6B8AF'
    },
    'COLOR_LIGHT_RED_3': {
        color: '#F4CCCC'
    },
    'COLOR_LIGHT_ORANGE_3': {
        color: '#FCE5CD'
    },
    'COLOR_LIGHT_YELLOW_3': {
        color: '#FFF2CC'
    },
    'COLOR_LIGHT_GREEN_3': {
        color: '#D9EAD3'
    },
    'COLOR_LIGHT_CYAN_3': {
        color: '#D0E0E3'
    },
    'COLOR_LIGHT_CORNFLOWER_BLUE_3': {
        color: '#C9DAF8'
    },
    'COLOR_LIGHT_BLUE_3': {
        color: '#CFE2F3'
    },
    'COLOR_LIGHT_PURPLE_3': {
        color: '#D9D2E9'
    },
    'COLOR_LIGHT_MAGENTA_3': {
        color: '#EAD1DC'
    },
    'COLOR_LIGHT_RED_BERRY_2': {
        color: '#DD7E6B'
    },
    'COLOR_LIGHT_RED_2': {
        color: '#EA9999'
    },
    'COLOR_LIGHT_ORANGE_2': {
        color: '#F9CB9C'
    },
    'COLOR_LIGHT_YELLOW_2': {
        color: '#FFE599'
    },
    'COLOR_LIGHT_GREEN_2': {
        color: '#B6D7A8'
    },
    'COLOR_LIGHT_CYAN_2': {
        color: '#A2C4C9'
    },
    'COLOR_LIGHT_CORNFLOWER_BLUE_2': {
        color: '#A4C2F4'
    },
    'COLOR_LIGHT_BLUE_2': {
        color: '#9FC5E8'
    },
    'COLOR_LIGHT_PURPLE_2': {
        color: '#B4A7D6'
    },
    'COLOR_LIGHT_MAGENTA_2': {
        color: '#D5A6BD'
    },
    'COLOR_LIGHT_RED_BERRY_1': {
        color: '#CC4125'
    },
    'COLOR_LIGHT_RED_1': {
        color: '#E06666'
    },
    'COLOR_LIGHT_ORANGE_1': {
        color: '#F6B26B'
    },
    'COLOR_LIGHT_YELLOW_1': {
        color: '#FFD966'
    },
    'COLOR_LIGHT_GREEN_1': {
        color: '#93C47D'
    },
    'COLOR_LIGHT_CYAN_1': {
        color: '#76A5AF'
    },
    'COLOR_LIGHT_CORNFLOWER_BLUE_1': {
        color: '#6D9EEB'
    },
    'COLOR_LIGHT_BLUE_1': {
        color: '#6FA8DC'
    },
    'COLOR_LIGHT_PURPLE_1': {
        color: '#8E7CC3'
    },
    'COLOR_LIGHT_MAGENTA_1': {
        color: '#C27BA0'
    },
    'COLOR_DARK_RED_BERRY_1': {
        color: '#A61C00'
    },
    'COLOR_DARK_RED_1': {
        color: '#CC0000'
    },
    'COLOR_DARK_ORANGE_1': {
        color: '#E69138'
    },
    'COLOR_DARK_YELLOW_1': {
        color: '#F1C232'
    },
    'COLOR_DARK_GREEN_1': {
        color: '#6AA84F'
    },
    'COLOR_DARK_CYAN_1': {
        color: '#45818E'
    },
    'COLOR_DARK_CORNFLOWER_BLUE_1': {
        color: '#3C78D8'
    },
    'COLOR_DARK_BLUE_1': {
        color: '#3D85C6'
    },
    'COLOR_DARK_PURPLE_1': {
        color: '#674EA7'
    },
    'COLOR_DARK_MAGENTA_1': {
        color: '#A64D79'
    },
    'COLOR_DARK_RED_BERRY_2': {
        color: '#85200C'
    },
    'COLOR_DARK_RED_2': {
        color: '#990000'
    },
    'COLOR_DARK_ORANGE_2': {
        color: '#B45F06'
    },
    'COLOR_DARK_YELLOW_2': {
        color: '#BF9000'
    },
    'COLOR_DARK_GREEN_2': {
        color: '#38761D'
    },
    'COLOR_DARK_CYAN_2': {
        color: '#134F5C'
    },
    'COLOR_DARK_CORNFLOWER_BLUE_2': {
        color: '#1155CC'
    },
    'COLOR_DARK_BLUE_2': {
        color: '#0B5394'
    },
    'COLOR_DARK_PURPLE_2': {
        color: '#351C75'
    },
    'COLOR_DARK_MAGENTA_2': {
        color: '#741B47'
    },
    'COLOR_DARK_RED_BERRY_3': {
        color: '#5B0F00'
    },
    'COLOR_DARK_RED_3': {
        color: '#660000'
    },
    'COLOR_DARK_ORANGE_3': {
        color: '#783F04'
    },
    'COLOR_DARK_YELLOW_3': {
        color: '#7F6000'
    },
    'COLOR_DARK_GREEN_3': {
        color: '#274E13'
    },
    'COLOR_DARK_CYAN_3': {
        color: '#0C343D'
    },
    'COLOR_DARK_CORNFLOWER_BLUE_3': {
        color: '#1C4587'
    },
    'COLOR_DARK_BLUE_3': {
        color: '#073763'
    },
    'COLOR_DARK_PURPLE_3': {
        color: '#20124D'
    },
    'COLOR_DARK_MAGENTA_3': {
        color: '#4C1130'
    },
};

const blockRenderMap = Map({
    'align-left': {
        element: 'div',
        wrapper: <div style={{ textAlign: 'left' }} />
    },
    'align-center': {
        element: 'div',
        wrapper: <div style={{ textAlign: 'center' }} />
    },
    'align-right': {
        element: 'div',
        wrapper: <div style={{ textAlign: 'right' }} />
    },
    'align-justify': {
        element: 'div',
        wrapper: <div style={{ textAlign: 'justify' }} />
    }
});

const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

type RichEditorProps = {
    pictures: null | Picture[],
    editorState: EditorState,
    setEditorState: any,
    action?: any,
    from_toolkit?: boolean,
    values: Record<string, unknown> & { [index: string]: string },
    contentContainerStyles?: React.CSSProperties,
    noDefault?: boolean,
    controlledState?: boolean,
    children?: (editor: JSX.Element) => JSX.Element,
    readOnly?: boolean,
    pictureAction?: null | (() => void),
    remaining_character?: number
} & EditorToolbarProps

const RichEditor = React.forwardRef<Editor, RichEditorProps>(({
    pictures,
    editorState,
    setEditorState,
    action,
    from_toolkit,
    values,
    contentContainerStyles,
    hideToolbar,
    noDefault,
    controlledState,
    children,
    toolbars,
    centerToolbars,
    readOnly,
    pictureAction,
    remaining_character
}, editorRef): ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const current_locale = useSelector((state: AppState) => state.locale.current_locale);
    const previous_locale = useSelector((state: AppState) => state.locale.previous_locale);
    const quotationCode = useSelector((state: AppState) => state.header.quotation_code);
    const styles = {
        ...RichEditorStyleMap,
        ...(quotationCode ? customColors[quotationCode] : null)
    };
    const refOfEditor = useRef(null);
    const handleKeyCommand = (command: any): DraftHandleValue => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            setEditorState(newState);
            return "handled";
        }
        return "not-handled";
    };
    const Image = (props: { src: string | undefined; width?: number ; }) => {
        return <img src={ props.src } style={{ width: props.width }} />;
    };
    const Media = (props: {
    contentState: { getEntity: (arg0: any) => any };
    block: { getEntityAt: (arg0: number) => any; getData: () => any };
    }) => {
        const entity = props.contentState.getEntity(props.block.getEntityAt(0));
        const type = entity.getType();
        const data = entity.getData();
        const alignment = props.block.getData().get('alignment')?.split('-')[1] || 'left';

        if (type === 'image') {
            return (
                <div style={{ textAlign: alignment as 'left' | 'center' | 'right' }}>
                    <Image src={data.src} width={data.width} />
                </div>
            );
        }

        return null;
    };
    const mediaBlockRenderer = (block: { getType: () => string; }) => {
        if (block.getType() === 'atomic') {
            return {
                component: Media,
                editable: false
            };
        }
    };

    useEffect(() => {
        if ( pictures && (pictures?.length ?? 0) > 0) {
            const newEditorState = imagePlugin.addImage(
                editorState,
                (pictures[0].thumbnail_big || pictures[0].url) ?? '',
                {
                    alt: pictures[0].name !== null ? pictures[0].name : '',
                    alignment: 'default',
                    width: 40,
                    height: 'auto'
                }
            );
            setEditorState(newEditorState);
        }
    }, [pictures]);

    const onSave = (save_locale: null | number) => (state?: EditorState) => {
        if (save_locale !== null) {
            const html = renderRichEditorContentHTML(
                styles,
                (state ?? editorState).getCurrentContent()
            );

            if (html.replace(/<[^>]+>/g, '') !== '\n' && action) {
                if (from_toolkit) {
                    dispatch(action({ id: save_locale, value: html }));
                } else {
                    const payload = action(save_locale, html);
                    if (payload) {
                        dispatch(payload);
                    }
                }
            }
        }
    };

    const handleChange = (newState: EditorState) => {
        // Draft.js conserve la même référence de ContentState quand seul le focus
        // ou la sélection change → on ne dispatche que si le contenu a réellement changé.
        const contentChanged = newState.getCurrentContent() !== editorState.getCurrentContent();
        setEditorState(newState);
        if (contentChanged) {
            onSave(current_locale)(newState);
        }
    };

    const hasMountedRef = useRef(false);
    useEffect(() => {
        if (current_locale !== null && !controlledState) {
            // Au montage, editorState est encore le placeholder vide créé par useState :
            // il ne représente aucune saisie réelle, donc on ne le flush pas vers previous_locale.
            if (hasMountedRef.current) {
                onSave(previous_locale)();
            }
            hasMountedRef.current = true;
            if (values[current_locale]) {
                const contentState = restoreRichEditorStateFromHtml(
                    styles,
                    values[current_locale]
                );
                setEditorState(EditorState.createWithContent(contentState.getCurrentContent(), decorator));
            } else {
                setEditorState(EditorState.createEmpty(decorator));
            }
        }
    }, [current_locale]);

    return (
        <Grid container className='ft-editor-container'>
            <AlignmentTool />
            <EditorToolbar
                editorState={editorState}
                setEditorState={handleChange}
                hideToolbar={hideToolbar}
                centerToolbars={centerToolbars}
                toolbars={toolbars}
                pictureAction={pictureAction}
            />
            <Grid
                item
                xs={12}
                sx={{ maxHeight: '45vh', overflowY: 'auto' }}
                onClick={() => (editorRef ?? refOfEditor)?.current?.focus()}
            >
                {
                    children ?
                        children(
                            <Editor
                                customStyleMap={{
                                    ...styles,
                                    ...RichEditorFontSizeStylesMap
                                }}
                                plugins={plugins}
                                // blockRendererFn={mediaBlockRenderer}
                                blockRenderMap={extendedBlockRenderMap}
                                editorState={editorState}
                                handleKeyCommand={handleKeyCommand}
                                onChange={handleChange}
                                ref={editorRef ?? refOfEditor}
                                readOnly={readOnly}
                            />
                        ) :
                        <div
                            className={'ft-editor'}
                            onBlur={() => onSave(current_locale)()}
                            style={{
                                ...(
                                    noDefault ?
                                        {
                                            padding: 0,
                                            border: 'none',
                                            minHeight: 0
                                        } :
                                        undefined
                                ),
                                ...contentContainerStyles,
                            }}
                        >
                            <Editor
                                customStyleMap={{
                                    ...styles,
                                    ...RichEditorFontSizeStylesMap
                                }}
                                plugins={plugins}
                                // blockRendererFn={mediaBlockRenderer}
                                blockRenderMap={extendedBlockRenderMap}
                                editorState={editorState}
                                handleKeyCommand={handleKeyCommand}
                                onChange={handleChange}
                                ref={editorRef ?? refOfEditor}
                            />
                            {remaining_character && isNumber(remaining_character) && <Typography variant="body2" color="textSecondary" className='richtext-input-adornment-fixed'>
                                {t('shared.remaining-character', { count: remaining_character })}
                            </Typography>}
                        </div>
                }
            </Grid>
        </Grid>
    );
});

RichEditor.displayName = 'RichEditor';

export default RichEditor;
